import React from "react";
import { PESSOAS } from "../data/team";
import { IconeSeta, IconeWhatsApp } from "./Icones";

/** Semana começando no domingo, como no calendário brasileiro. */
const CABECALHO_SEMANA = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

function colunaDaSemana(diaSemana) {
  return diaSemana;
}

function descricaoDoDia(d) {
  const partes = [`${d.dia} de ${d.diaSemanaLongo}`, d.pessoa.nome];
  if (d.feriado) partes.push(d.feriado);
  else if (d.fimDeSemana) partes.push("fim de semana");
  if (d.troca) partes.push(`troca com ${PESSOAS[d.troca.titular].primeiroNome}`);
  return partes.join(", ");
}

/**
 * A escala do mês em duas leituras da mesma informação:
 * calendário na tela larga, onde a forma do mês ajuda a enxergar os fins de
 * semana, e lista no celular, onde uma grade de sete colunas ficaria ilegível.
 * O filtro por pessoa responde "quando é a minha vez?" sem varrer o mês.
 */
export default function EscalaMes({
  rotulo,
  dias,
  time,
  filtro,
  onFiltro,
  onMes,
  onAno,
  onHoje,
  chaveHoje,
  noMesAtual,
  limites,
  proximo,
}) {
  const visiveis = filtro ? dias.filter((d) => d.responsavel === filtro) : dias;
  const vazios = dias.length ? colunaDaSemana(dias[0].diaSemana) : 0;

  const classesCelula = (d) =>
    [
      "celula",
      d.chave === chaveHoje && "celula--hoje",
      (d.fimDeSemana || d.feriado) && "celula--pesado",
      filtro && d.responsavel !== filtro && "celula--apagada",
    ]
      .filter(Boolean)
      .join(" ");

  return (
    <section className="escala" aria-labelledby="escala-titulo">
      <header className="secao__cabecalho escala__cabecalho">
        <div>
          <h2 id="escala-titulo">Escala do mês</h2>
          <p className="secao__descricao">
            O rodízio anda um nome por dia e não reinicia no fim do mês.
          </p>
        </div>

        <div className="navmes" role="group" aria-label="Navegar na escala">
          <button
            type="button"
            className="botao botao__icone"
            onClick={() => onAno(-1)}
            disabled={!limites.podeVoltar}
            aria-label="Ano anterior"
          >
            <IconeSeta direcao="esquerda" duplo />
          </button>
          <button
            type="button"
            className="botao botao__icone"
            onClick={() => onMes(-1)}
            disabled={!limites.podeVoltar}
            aria-label="Mês anterior"
          >
            <IconeSeta direcao="esquerda" />
          </button>
          <span className="navmes__rotulo" aria-live="polite">
            {rotulo}
          </span>
          <button
            type="button"
            className="botao botao__icone"
            onClick={() => onMes(1)}
            disabled={!limites.podeAvancar}
            aria-label="Próximo mês"
          >
            <IconeSeta direcao="direita" />
          </button>
          <button
            type="button"
            className="botao botao__icone"
            onClick={() => onAno(1)}
            disabled={!limites.podeAvancar}
            aria-label="Próximo ano"
          >
            <IconeSeta direcao="direita" duplo />
          </button>
        </div>
      </header>

      <div className="filtros" role="group" aria-label="Filtrar por pessoa">
        <button
          type="button"
          className={`chip chip--texto ${!filtro ? "chip--ativo" : ""}`}
          aria-pressed={!filtro}
          onClick={() => onFiltro(null)}
        >
          Todos
        </button>
        {time.map((id) => {
          const pessoa = PESSOAS[id];
          const ativo = filtro === id;
          return (
            <button
              key={id}
              type="button"
              className={`chip ${ativo ? "chip--ativo" : ""}`}
              aria-pressed={ativo}
              onClick={() => onFiltro(ativo ? null : id)}
            >
              <img className="avatar avatar--xs" src={pessoa.foto} alt="" />
              {pessoa.primeiroNome}
            </button>
          );
        })}
        {!noMesAtual && (
          <button
            type="button"
            className="chip chip--texto chip--acao"
            onClick={onHoje}
          >
            Voltar para hoje
          </button>
        )}
      </div>

      {filtro && proximo && (
        <p className="proximo" aria-live="polite">
          Próximo plantão de {PESSOAS[filtro].primeiroNome}:{" "}
          <strong>{proximo.dataLonga}</strong>, {proximo.diaSemanaLongo}
          {proximo.feriado ? ` — ${proximo.feriado}` : ""}
        </p>
      )}

      {/* Tela larga: o mês como calendário. */}
      <div className="calendario">
        <div className="calendario__semana" aria-hidden="true">
          {CABECALHO_SEMANA.map((d) => (
            <span key={d} className="calendario__coluna">
              {d}
            </span>
          ))}
        </div>
        <div className="calendario__grade">
          {Array.from({ length: vazios }, (_, i) => (
            <span key={`v${i}`} className="celula celula--vazia" />
          ))}
          {dias.map((d) => (
            <a
              key={d.chave}
              className={classesCelula(d)}
              href={d.pessoa.whatsapp}
              target="_blank"
              rel="noreferrer"
              title={descricaoDoDia(d)}
              aria-label={`Chamar ${d.pessoa.primeiroNome} no WhatsApp — plantão de ${descricaoDoDia(
                d
              )}`}
            >
              <span className="celula__topo">
                <span className="celula__numero">{d.dia}</span>
                {d.feriado && (
                  <span className="celula__feriado">{d.feriado}</span>
                )}
                {d.troca && !d.feriado && (
                  <span className="celula__feriado celula__feriado--troca">
                    troca
                  </span>
                )}
              </span>
              <span className="celula__pessoa">
                <img className="avatar avatar--xs" src={d.pessoa.foto} alt="" />
                <span className="celula__nome">{d.pessoa.primeiroNome}</span>
              </span>
            </a>
          ))}
        </div>
      </div>

      {/* Celular — e a leitura acessível em qualquer tela. */}
      <ol className="dias">
        {visiveis.map((d) => {
          const hoje = d.chave === chaveHoje;
          const pesado = d.fimDeSemana || d.feriado;
          return (
            <li
              key={d.chave}
              className={`dia ${hoje ? "dia--hoje" : ""} ${
                pesado ? "dia--pesado" : ""
              }`}
            >
              <span className="dia__data">
                <span className="dia__numero">
                  {String(d.dia).padStart(2, "0")}
                </span>
                <span className="dia__semana">{d.diaSemanaCurto}</span>
              </span>

              <span className="dia__pessoa">
                <img className="avatar avatar--sm" src={d.pessoa.foto} alt="" />
                <span className="dia__nome">
                  {d.pessoa.nome}
                  {hoje && <span className="dia__marca">hoje</span>}
                </span>
              </span>

              <span className="dia__selos">
                {d.feriado && <span className="selo selo--pesado">{d.feriado}</span>}
                {d.fimDeSemana && !d.feriado && (
                  <span className="selo selo--pesado">FDS</span>
                )}
                {d.troca && (
                  <span
                    className="selo selo--troca"
                    title={`No lugar de ${
                      PESSOAS[d.troca.titular].primeiroNome
                    }${d.troca.motivo ? ` · ${d.troca.motivo}` : ""}`}
                  >
                    Troca
                  </span>
                )}
              </span>

              <a
                className="botao botao__icone botao--discreto dia__acao"
                href={d.pessoa.whatsapp}
                target="_blank"
                rel="noreferrer"
                aria-label={`Chamar ${d.pessoa.primeiroNome} no WhatsApp — plantão de ${descricaoDoDia(
                  d
                )}`}
              >
                <IconeWhatsApp size={16} />
              </a>
            </li>
          );
        })}
      </ol>

      {visiveis.length === 0 && (
        <p className="vazio">
          {PESSOAS[filtro]?.primeiroNome} não tem plantão em {rotulo}. Use as
          setas para procurar em outro mês.
        </p>
      )}
    </section>
  );
}
